import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatPrice(price: number, currency: string = 'USD'): string {
  const currencySymbols: Record<string, string> = {
    USD: '$',
    CLP: '$',
    EUR: '€'
  };
  
  const symbol = currencySymbols[currency] || '$';
  
  if (currency === 'CLP') {
    return `${symbol}${price.toLocaleString('es-CL')}`;
  }
  
  return `${symbol}${price.toLocaleString('en-US')}`;
}

export function formatArea(area: number): string {
  return `${area} m²`;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

export function formatDate(date: string, locale: string = 'es'): string {
  const dateObj = new Date(date);
  
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  
  return dateObj.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', options);
}

export function getReadingTime(text: string): number {
  const wordsPerMinute = 200;
  const words = text.split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
}

export function generateWhatsAppLink(phone: string, message: string): string {
  const cleanPhone = phone.replace(/[^\d]/g, '');
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
}

export function generatePropertyShareLinks(property: any, baseUrl: string = '') {
  const url = `${baseUrl}/propiedad/${property.id}`;
  const title = property.title;
  const description = `${property.title} - ${formatPrice(property.price, property.currency)}`;

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
    email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(description + ' ' + url)}`
  };
}

/**
 * Pluraliza palabras en español correctamente
 * Maneja reglas gramaticales del español para pluralización
 */
export function pluralize(word: string, count: number): string {
  if (count === 1) return word;

  // Casos especiales con formas irregulares
  const irregularPlurals: Record<string, string> = {
    'propiedad': 'propiedades',
    'ciudad': 'ciudades',
    'universidad': 'universidades',
    'comunidad': 'comunidades',
    'cantidad': 'cantidades',
    'calidad': 'calidades',
    'sociedad': 'sociedades',
    'edad': 'edades',
    'libertad': 'libertades',
    'actividad': 'actividades'
  };

  const lowercaseWord = word.toLowerCase();
  if (irregularPlurals[lowercaseWord]) {
    return irregularPlurals[lowercaseWord];
  }

  // Reglas generales de pluralización en español
  // 1. Termina en vocal no acentuada → añadir "s"
  if (/[aeiou]$/.test(word)) {
    return word + 's';
  }

  // 2. Termina en consonante, "y" o vocal acentuada → añadir "es"
  if (/[^aeiou]$/.test(word) || /[áéíóú]$/.test(word)) {
    return word + 'es';
  }

  // 3. Termina en "z" → cambiar "z" por "ces"
  if (/z$/.test(word)) {
    return word.slice(0, -1) + 'ces';
  }

  // Por defecto, añadir "s"
  return word + 's';
}

/**
 * Formatea un contador con el nombre del item pluralizado
 * Incluye concordancia de género para adjetivos
 */
export function formatCountWithItem(
  count: number,
  itemName: string,
  adjective: string = 'encontrado',
  gender: 'm' | 'f' = 'f'
): string {
  const pluralItem = pluralize(itemName, count);
  const pluralAdjective = count === 1
    ? (gender === 'm' ? adjective : adjective.replace(/o$/, 'a'))
    : (gender === 'm' ? adjective + 's' : adjective.replace(/o$/, 'a') + 's');

  return `${count} ${count === 1 ? itemName : pluralItem} ${pluralAdjective}`;
}